import { BodyBox, EntityForm } from '@lbcamden/lib-react';
import { FunctionComponent, useMemo } from 'react';

import { PageBreadcrumbs } from '~/client/components/shared/PageBreadcrumbs';
import {
  useGetPersonFullName,
  useReferenceListSelectOptions,
  useUserStore,
} from '~/client/hooks/storeHooks';
import { useGoBackOrNavigate } from '~/client/hooks/useGoBackOrNavigate';
import { getPropertyBreadcrumbs, toPersonPage } from '~/client/utils/navigation';
import { HouseholdMemberUpdate } from '~/common/types/people';

import { LoadInNotFoundPage } from '../../LoadInNotFoundPage';
import {
  householdMemberUpdateFormSchema,
  HouseholdMemberUpdateFormValues,
  personFormConfig,
  PersonFormValues,
  PersonUpdateFormValues,
  tenantUpdateFormSchema,
} from '../formConfig';
import { usePersonFromPath } from '../usePersonFromPath';

export const PersonEditPage: FunctionComponent = () => {
  const navigate = useGoBackOrNavigate();

  const userStore = useUserStore();
  const refListSelectOptions = useReferenceListSelectOptions();
  const getPersonFullName = useGetPersonFullName();
  const { property, person } = usePersonFromPath();

  const formConfig = useMemo(
    () => personFormConfig(refListSelectOptions, person?.isTenant),
    [refListSelectOptions, person],
  );

  if (!property || !person) {
    return <LoadInNotFoundPage />;
  }

  const { personId, propertyId, isTenant } = person;
  const schema = isTenant ? tenantUpdateFormSchema : householdMemberUpdateFormSchema;
  const title = `Edit ${isTenant ? 'tenant' : 'household member'}`;

  const goToPersonPage = () => navigate(toPersonPage(propertyId, personId));

  const onSave = (update: PersonUpdateFormValues) => {
    // Tenant's names cannot be changed so skip if person is a tenant
    if (!isTenant) {
      (update as HouseholdMemberUpdate).fullName = getPersonFullName(
        update as HouseholdMemberUpdateFormValues,
      );
    }
    const messageType = isTenant ? 'updateTenant' : 'updateHouseholdMember';
    userStore?.handleAndDispatchMessage(messageType, personId, update);
    void goToPersonPage();
  };

  return (
    <BodyBox>
      <PageBreadcrumbs
        css={{ marginBottom: 0 }}
        links={[
          ...getPropertyBreadcrumbs(property),
          { link: toPersonPage(propertyId, personId), name: person.fullName },
          { name: title },
        ]}
      />
      <EntityForm<PersonUpdateFormValues, PersonFormValues>
        title={title}
        schema={schema}
        config={formConfig}
        initialValue={person}
        onCancel={goToPersonPage}
        onSave={onSave}
      />
    </BodyBox>
  );
};
